import React from 'react';
import { Book, Cloud } from '@readcloud/data';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetClose,
  SheetFooter,
} from '@readcloud/ui/sheet';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@readcloud/ui/accordion';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
} from '@readcloud/ui/alert-dialog';
import { Button } from '@readcloud/ui/button';
import { authSelectors, cloudSelectors } from '@readcloud/state';
import { useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@readcloud/ui/form';
import { useReadCloudAuth } from '@readcloud/common';
import { useCloudsAndContentActions } from '../context/ActionsContext';
import { useUploadMutations } from './upload/hooks/useUploadMutations';
import { LoadingIcon } from './LoadingIcon/LoadingIcon';
import { SelectContentType } from './upload/SelectContentType';
import UploadThumbnail from './upload/UploadThumbnail';
import AddContentDetails from './upload/AddContentDetails';
import SelectClouds from './upload/SelectClouds';
import { MdDelete } from "react-icons/md";
import {
  contentFieldSchema,
  thumbnailFieldSchema,
  contentDetailsFieldSchema,
  cloudsFieldSchema,
} from './upload/schemas/formFields';

// Additional schemas for update-related fields (the fields we send to create vs update are different...)
const contentSpecificSchema = z.object({
  bookId: z.string(),
  downloadURI: z.string().optional(),
  thumbnailURI: z.string().optional(),
  type: z.string().optional(),
  authorName: z.string().optional(),
  cloudAction: z.enum(['replace', 'add', 'remove']).default('replace'),
});

const contentSettingsSchema = z.object({
  ...contentDetailsFieldSchema.shape,
  ...cloudsFieldSchema.shape,
  ...contentSpecificSchema.shape,
});

type ContentSettingsFormData = z.infer<typeof contentSettingsSchema>;

interface ContentSettingsSheetProps {
  book: Book | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ContentSettingsSheet({
  book,
  open,
  onOpenChange,
}: ContentSettingsSheetProps) {
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const { user } = useReadCloudAuth();
  const actions = useCloudsAndContentActions();
  const { updateContentMutation, updateBookDeletedMutation } = useUploadMutations(user);
  const [showCloseAlert, setShowCloseAlert] = React.useState(false);
  const clouds = useSelector(cloudSelectors.getClouds);
  const allClouds = useSelector(cloudSelectors.getInstitutionClouds);
  const userPermissions = useSelector(authSelectors.getUserPermissions);
  const availableClouds: Record<string, Cloud> = React.useMemo(() => {
    return userPermissions?.['institutionClouds']?.read ? allClouds : clouds;
  }, [clouds, allClouds, user]);

  const methods = useForm<ContentSettingsFormData>({
    resolver: zodResolver(contentSettingsSchema),
  });

  // Reset book-related form values when book changes
  React.useEffect(() => {
    if (book) {
      methods.reset({
        bookId: book.id,
        type: book.type || '',
        downloadURI: book.downloadURI || '',
        thumbnailURI: book.imageThumbnailURI || '',
        title: book.title || '',
        description: book.description || '',
        printable: book.options?.printable || false,
        comments: !book.options?.disableComments,
        authorName: book.authorName || '',
        cloudAction: 'replace',
      });
    }
  }, [book, methods]);

  // Only reset cloud selections when available clouds change
  React.useEffect(() => {
    if (book && availableClouds) {
      methods.setValue(
        'clouds',
        Object.values(availableClouds)
          .filter((cloud) => cloud.books.some((b) => b === book.id))
          .map((cloud) => cloud.id)
      );
    }
  }, [book, availableClouds, methods]);

  const handleSubmit = async (data: ContentSettingsFormData) => {
    try {
      setSubmitting(true);

      // Transform the form data to match the API payload shape
      const payload = {
        id: data.bookId,
        title: data.title,
        downloadURI: data.downloadURI || book?.downloadURI || '',
        description: data.description || '',
        authorName: data.authorName || '',
        imageThumbnailURI: data.thumbnailURI || book?.imageThumbnailURI || '',
        type: data.type || book?.type || 'EBook',
        options: {
          disableComments: !data.comments,
          printable: data.printable,
        },
        cloudIds: data.clouds,
        cloudAction: data.cloudAction,
        fileSize: book?.fileSize || 0,
        created: book?.created || new Date().toISOString(),
        updated: new Date().toISOString(),
        deleted: book?.deleted || false,
      };

      await updateContentMutation.mutateAsync(payload);

      actions.refresh();
      onOpenChange(false);
    } catch (err) {
      console.error('Error details:', err);
      setError('Failed to update book settings. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!book) return;
    try {
      setSubmitting(true);
      await updateBookDeletedMutation.mutateAsync({ bookId: book.id, deleted: true });
      actions.refresh();
      setShowCloseAlert(false);
      onOpenChange(false);
    } catch (err) {
      console.error('Error details:', err);
      setError('Failed to delete book. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="rc-overflow-y-auto rc-z-[51] max-sm:rc-w-[90%] [&>button.rc-absolute]:rc-top-[calc(1rem+env(safe-area-inset-top))]">
        <div className="rc-flex rc-flex-col rc-h-full">
          <SheetHeader
            className="rc-text-left"
            style={{ paddingTop: 'env(safe-area-inset-top)' }}
          >
            <SheetTitle>Content Settings</SheetTitle>
            <SheetDescription>
              Make changes to your content here. Click save when you're done.
            </SheetDescription>
          </SheetHeader>

            <FormProvider {...methods}>
              <form
                id="content-settings-form"
                onSubmit={methods.handleSubmit(handleSubmit)}
                className="rc-flex rc-flex-col rc-flex-1"
              >
                <div className="rc-space-y-6 rc-py-4 rc-flex-1">
                  <Accordion type="multiple" defaultValue={['details', 'clouds']}>
                    <AccordionItem value="details">
                      <AccordionTrigger>Details</AccordionTrigger>
                      <AccordionContent className="rc-pt-2">
                        <AddContentDetails
                          type={'default'}
                          thumbnail={methods.watch('thumbnailURI')}
                        />
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="clouds">
                      <AccordionTrigger>Clouds</AccordionTrigger>
                      <AccordionContent className="rc-pt-2">
                        <SelectClouds />
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>

                {submitting && (
                  <LoadingIcon
                    loading
                    backdrop
                    position="absolute"
                    className="rc-z-[2000] rc-overflow-hidden rc-absolute rc-inset-0"
                  />
                )}

              <SheetFooter className="rc-pb-6 rc-gap-3"
                style={{
                  paddingBottom:
                    'calc(1.5rem + env(safe-area-inset-bottom))',
                }}>
                  <Button type="button" variant="destructive" onClick={()=> {
                    setShowCloseAlert(true);
                  }}>
                    <MdDelete /> Delete
                  </Button>

                  <div className='rc-flex rc-gap-2'>
                    <SheetClose asChild>
                      <Button variant="outline" type="button">
                        Cancel
                      </Button>
                    </SheetClose>
                    <Button type="submit" disabled={submitting}>
                      Save changes
                    </Button>
                  </div>
                </SheetFooter>
                <AlertDialog open={showCloseAlert} onOpenChange={() => { setShowCloseAlert(false); onOpenChange?.(false); }}>
                  <AlertDialogPortal>
                    <AlertDialogOverlay className="!rc-z-[52]" />
                    <AlertDialogContent className='!rc-z-[53] max-md:rc-w-[96%] rc-rounded-lg'>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you sure you want to delete?</AlertDialogTitle>
                        <AlertDialogDescription>
                         Delete this content?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <Button variant="outline" onClick={() => setShowCloseAlert(false)}>
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={handleDelete}
                        >
                          Delete
                        </Button>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialogPortal>
                </AlertDialog>
              </form>
            </FormProvider>
          </div>
        </SheetContent>
      </Sheet>
      
    </>
  );
}
